import React from 'react';
import Style from './Governance.module.css';

// Version-lifecycle banner shown at the top of the editor. It surfaces the
// working version + its status, a Draft → Approved → Published stepper, and the
// SINGLE transforming primary action:
//   dirty (unsaved edits) → Save Draft
//   clean draft (DRAFT / REJECTED) → Submit for Approval
//   approved → Publish
// Reject is a secondary action (a DRAFT or APPROVED version can be rejected) and
// Cancel is the only control that returns to the landing page. The parent
// (GovernanceEditor) owns the confirmation popups; this component only invokes
// the callbacks.

const STATUS_BADGE = {
  DRAFT:     { label: 'Draft',     cls: 'moderate' },
  APPROVED:  { label: 'Approved',  cls: 'low' },
  REJECTED:  { label: 'Rejected',  cls: 'critical' },
  PUBLISHED: { label: 'Published', cls: 'low' },
  OBSOLETE:  { label: 'Obsolete',  cls: 'neutral' },
};

const STEPS = [
  { key: 'DRAFT',     label: 'Draft' },
  { key: 'APPROVED',  label: 'Approved' },
  { key: 'PUBLISHED', label: 'Published' },
];

// Control-type-specific note copy. Defaults are the exact Data Controls strings,
// so the DATA editor (which passes no `labels`) renders byte-identically; the
// WEB/AI editor passes its own copy ("app" instead of "rule", no default rules).
const DEFAULT_LABELS = {
  newPolicy: 'New policy — click Save Draft to create it with the default rules.',
  editing: 'Editing the published policy — change a rule to start a new draft.',
  rejected: 'Rejected — revise the rules and resubmit for approval.',
};

const PolicyVersionBanner = ({
  statusCode, versionNumber, draftExists, dirty, allowInitialSave,
  onSaveDraft, onSubmitForApproval, onReject, onPublish, onCancel,
  saving, publishing, disabled, labels,
}) => {
  const copy = { ...DEFAULT_LABELS, ...(labels || {}) };
  const status = statusCode || 'DRAFT';
  const badge = STATUS_BADGE[status] || { label: status, cls: 'neutral' };
  const rejected = status === 'REJECTED';

  // A stepper node turns green (done) only once its phase is genuinely complete:
  //   • Draft:     a draft exists AND has no unsaved edits — adding a rule
  //                (dirty) reverts this node from green back to the current/normal
  //                colour until Save Draft is clicked.
  //   • Approved:  the version is approved (or already published).
  //   • Published: the version is published.
  // The first not-yet-done node is the "current" (highlighted) step.
  const done = [
    draftExists && !dirty && !rejected,               // Draft
    status === 'APPROVED' || status === 'PUBLISHED',  // Approved
    status === 'PUBLISHED',                           // Published
  ];
  const currentIdx = done.indexOf(false); // -1 → all done (published)

  const stepClass = (i) => {
    if (rejected && i === 0) return `${Style.step} ${Style.stepRejected}`;
    if (done[i]) return `${Style.step} ${Style.stepDone}`;
    if (i === currentIdx) return `${Style.step} ${Style.stepActive}`;
    return `${Style.step} ${Style.stepTodo}`;
  };

  // Status-aware note.
  let note;
  if (allowInitialSave) {
    note = dirty
      ? 'Unsaved changes — click Save Draft to create the policy.'
      : copy.newPolicy;
  } else if (!draftExists) {
    note = dirty
      ? 'Unsaved changes — click Save Draft to create the draft.'
      : copy.editing;
  } else if (dirty) {
    note = 'Unsaved changes — save the draft before submitting for approval.';
  } else if (status === 'REJECTED') {
    note = copy.rejected;
  } else if (status === 'APPROVED') {
    note = 'Approved — ready to publish, or reject to revise.';
  } else {
    note = 'Draft saved — submit it for approval when ready.';
  }

  // The single transforming primary button.
  let primary = null;
  if (dirty || allowInitialSave) {
    primary = (
      <button className={`${Style.btn} ${Style.btnPrimary}`} onClick={onSaveDraft} disabled={disabled}>
        {saving ? 'Saving…' : 'Save Draft'}
      </button>
    );
  } else if (status === 'APPROVED') {
    primary = (
      <button className={`${Style.btn} ${Style.btnPrimary}`} onClick={onPublish} disabled={disabled}>
        {publishing ? 'Publishing…' : 'Publish'}
      </button>
    );
  } else if (draftExists && (status === 'DRAFT' || status === 'REJECTED')) {
    primary = (
      <button className={`${Style.btn} ${Style.btnPrimary}`} onClick={onSubmitForApproval} disabled={disabled}>
        {saving ? 'Submitting…' : 'Submit for Approval'}
      </button>
    );
  }

  // Reject acts on the persisted version, so only offer it once the draft is
  // saved in the DB (exists) and has no unsaved local edits (clean). While dirty
  // the primary action is Save Draft, not Reject.
  const canReject = draftExists && !dirty && (status === 'DRAFT' || status === 'APPROVED');

  return (
    <div className={`${Style.versionBanner} ${rejected ? Style.versionBannerRejected : ''}`}>
      <div className={Style.versionBannerInfo}>
        <div className={Style.versionBannerTitleRow}>
          <span className={Style.versionBannerTitle}>
            Working version{versionNumber ? ` v${versionNumber}` : ''}
          </span>
          <span className={`${Style.badge} ${Style[badge.cls]}`}>{badge.label}</span>
        </div>
        <div className={Style.versionBannerSubtitle}>{note}</div>
        <ol className={Style.stepper}>
          {STEPS.map((s, i) => (
            <li key={s.key} className={stepClass(i)}>
              <span className={Style.stepDot}>{i + 1}</span>
              <span className={Style.stepLabel}>{s.label}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className={Style.versionBannerActions}>
        {primary}
        {canReject && (
          <button className={Style.btn} onClick={onReject} disabled={disabled}>
            Reject
          </button>
        )}
        {/* Cancel is the only control that returns to the landing page. */}
        <button className={Style.btn} onClick={onCancel} disabled={disabled}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PolicyVersionBanner;
